"use client";

import { type FC, type ReactNode, useCallback, useEffect, useRef } from "react";
import { type LogColorType, log as baseLog } from "@huub-web/utils";

import { DebugContext } from "../../contexts/DebugContext";
import { useLocalState } from "../../hooks";

/**
 * Props for the DebugProvider component
 */
export type DebugProviderProps = {
  debug: boolean;
  children: ReactNode;
};

/**
 * Provider component for the Debug context.
 *
 * `log` and `info` are referentially stable — they read the current
 * debug flag from refs so consumers that include them in dependency
 * arrays (useCallback, useEffect, etc.) are not recreated when the
 * debug flag toggles.
 */
export const DebugProvider: FC<DebugProviderProps> = ({ debug, children }) => {
  const [debugOverride] = useLocalState<boolean>("debug", false);

  /**
   * The localStorage `debug` override is honoured only outside production
   * builds. In production it would let any visitor flip `localStorage.debug`
   * and stream verbose API request/response bodies (income, applicant PII)
   * and WebSocket payloads to the console, defeating Sentry's PII scrubbing
   * (SMA-2579). The build-time `debug` prop is unaffected.
   */
  const debugOverrideEnabled =
    process.env.NODE_ENV !== "production" && debugOverride;

  const debugRef = useRef(debug);
  debugRef.current = debug;
  const debugOverrideRef = useRef(debugOverrideEnabled);
  debugOverrideRef.current = debugOverrideEnabled;

  const log = useCallback(
    (color: LogColorType, realm: string | string[], ...args: unknown[]) => {
      if (!debugRef.current && !debugOverrideRef.current) {
        return;
      }
      baseLog(color, realm, ...args);
    },
    [],
  );

  const info = useCallback((...args: unknown[]) => {
    if (!debugRef.current && !debugOverrideRef.current) {
      return;
    }
    console.info(...args);
  }, []);

  useEffect(() => {
    if (debugOverrideEnabled || debug) {
      log("yellow", "DebugProvider", "Debug enabled", {
        debugOverride: debugOverrideEnabled,
        debug,
      });
    }
  }, [debugOverrideEnabled, debug, log, info]);

  return (
    <DebugContext.Provider
      value={{ debug: debugOverrideEnabled || debug, log, info }}
    >
      {children}
    </DebugContext.Provider>
  );
};
